"use client";

import { useState } from "react";
import type { AiProfile } from "@/types/ai";
import { db } from "@/lib/db";

interface Props {
  profile: AiProfile;
  onUpdate: (profile: AiProfile) => void;
  /** When "builder", skip direct db write; only call onUpdate so builder controls save. */
  variant?: "standalone" | "builder";
}

/**
 * Ultra Navigation Bar Customizer
 * 
 * Allows users to fully customize the Ultra Navigation Bar:
 * - Enable/disable features
 * - Adjust aura effects
 * - Customize text and placeholders
 * - Control position and size
 */
export default function UltraNavCustomizer({ profile, onUpdate, variant = "standalone" }: Props) {
  const config = profile.brand?.ultraNavConfig || {};
  
  const [localConfig, setLocalConfig] = useState({
    enabled: config.enabled !== false,
    showBuilder: config.showBuilder !== false,
    showMarketplace: config.showMarketplace !== false,
    showSettings: config.showSettings !== false,
    showVoice: config.showVoice !== false,
    showResponseMode: config.showResponseMode !== false,
    showAttachment: config.showAttachment !== false,
    showTemplates: config.showTemplates !== false,
    auraEnabled: config.auraEnabled !== false,
    auraIntensity: config.auraIntensity ?? 0.5,
    auraSpeed: config.auraSpeed || "normal",
    restingStateCTA: config.restingStateCTA || profile.name || "Build Your Dream",
    placeholder: config.placeholder || "",
    position: config.position || "bottom",
    size: config.size || "medium",
  });

  const primaryColor = profile.brand?.primaryColor || "#87CEEB";
  const secondaryColor = profile.brand?.secondaryColor || "#B0E0E6";
  const textColor = profile.brand?.textColor || "#000000";

  async function handleConfigChange(key: string, value: any) {
    const updated = {
      ...localConfig,
      [key]: value,
    };
    setLocalConfig(updated);

    // Save to profile
    const updatedProfile = {
      ...profile,
      brand: {
        ...profile.brand,
        ultraNavConfig: updated,
      },
      updatedAt: Date.now(),
    };

    try {
      if (variant !== "builder" && typeof window !== "undefined" && db && db.aiProfiles) {
        await db.aiProfiles.update(profile.id, updatedProfile);
      }
      onUpdate(updatedProfile);
    } catch (error) {
      console.error("Failed to save Ultra Nav config:", error);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2" style={{ color: textColor }}>
          Ultra Navigation Bar
        </h2>
        <p className="text-sm mb-6" style={{ color: profile.brand?.textSecondaryColor || "rgba(0, 0, 0, 0.7)" }}>
          Customize your navigation bar's appearance, features, and behavior.
        </p>
      </div>

      {/* Enable/Disable */}
      <div className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm" style={{
        border: `1px solid ${primaryColor}20`,
        boxShadow: `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`,
      }}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-bold text-lg mb-1" style={{ color: textColor }}>
              Enable Ultra Navigation Bar
            </h3>
            <p className="text-sm" style={{ color: profile.brand?.textSecondaryColor || "rgba(0, 0, 0, 0.7)" }}>
              Toggle the entire navigation bar on/off
            </p>
          </div>
          <button
            onClick={() => handleConfigChange("enabled", !localConfig.enabled)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              localConfig.enabled
                ? ""
                : "bg-gray-300"
            }`}
            style={
              localConfig.enabled
                ? {
                    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                  }
                : {}
            }
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-lg ${
                localConfig.enabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>

      {/* Feature Toggles */}
      {localConfig.enabled && (
        <>
          <div className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm" style={{
            border: `1px solid ${primaryColor}20`,
            boxShadow: `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`,
          }}>
            <h3 className="font-bold text-lg mb-4" style={{ color: textColor }}>
              Visible Features
            </h3>
            <div className="space-y-3">
              {[
                { key: "showBuilder", label: "Builder Button", desc: "Show builder access button" },
                { key: "showMarketplace", label: "Widgets & APIs", desc: "Show widgets/marketplace button" },
                { key: "showSettings", label: "Settings Button", desc: "Show settings access button" },
                { key: "showVoice", label: "Voice Input", desc: "Show voice command button" },
                { key: "showResponseMode", label: "Response Mode Selector", desc: "Show response mode selector" },
                { key: "showAttachment", label: "Attachment Button", desc: "Show file/image attachment button" },
                { key: "showTemplates", label: "Templates Button", desc: "Show template/builder quick access" },
              ].map((feature) => (
                <div key={feature.key} className="flex items-center justify-between py-2">
                  <div>
                    <div className="font-medium text-sm" style={{ color: textColor }}>
                      {feature.label}
                    </div>
                    <div className="text-xs" style={{ color: profile.brand?.textSecondaryColor || "rgba(0, 0, 0, 0.6)" }}>
                      {feature.desc}
                    </div>
                  </div>
                  <button
                    onClick={() => handleConfigChange(feature.key, !localConfig[feature.key as keyof typeof localConfig])}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      localConfig[feature.key as keyof typeof localConfig]
                        ? ""
                        : "bg-gray-300"
                    }`}
                    style={
                      localConfig[feature.key as keyof typeof localConfig]
                        ? {
                            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                          }
                        : {}
                    }
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                        localConfig[feature.key as keyof typeof localConfig] ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Aura Settings */}
          <div className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm" style={{
            border: `1px solid ${primaryColor}20`,
            boxShadow: `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`,
          }}>
            <h3 className="font-bold text-lg mb-4" style={{ color: textColor }}>
              Aura Effects
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm mb-1" style={{ color: textColor }}>
                    Enable Aura Glow
                  </div>
                  <div className="text-xs" style={{ color: profile.brand?.textSecondaryColor || "rgba(0, 0, 0, 0.6)" }}>
                    Pulsating glow effect around the bar
                  </div>
                </div>
                <button
                  onClick={() => handleConfigChange("auraEnabled", !localConfig.auraEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    localConfig.auraEnabled ? "" : "bg-gray-300"
                  }`}
                  style={
                    localConfig.auraEnabled
                      ? {
                          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                        }
                      : {}
                  }
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                      localConfig.auraEnabled ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {localConfig.auraEnabled && (
                <>
                  <div>
                    <div className="flex justify-between mb-2">
                      <span className="text-sm font-medium" style={{ color: textColor }}>
                        Aura Intensity
                      </span>
                      <span className="text-sm" style={{ color: profile.brand?.textSecondaryColor || "rgba(0, 0, 0, 0.7)" }}>
                        {Math.round(localConfig.auraIntensity * 100)}%
                      </span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={localConfig.auraIntensity}
                      onChange={(e) => handleConfigChange("auraIntensity", parseFloat(e.target.value))}
                      className="w-full h-2 rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                      }}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-2 block" style={{ color: textColor }}>
                      Aura Speed
                    </label>
                    <div className="flex gap-2">
                      {(["slow", "normal", "fast"] as const).map((speed) => (
                        <button
                          key={speed}
                          onClick={() => handleConfigChange("auraSpeed", speed)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            localConfig.auraSpeed === speed
                              ? "text-white"
                              : "text-gray-700"
                          }`}
                          style={
                            localConfig.auraSpeed === speed
                              ? {
                                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                                }
                              : {
                                  background: "rgba(0, 0, 0, 0.05)",
                                }
                          }
                        >
                          {speed.charAt(0).toUpperCase() + speed.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Text Customization */}
          <div className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm" style={{
            border: `1px solid ${primaryColor}20`,
            boxShadow: `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`,
          }}>
            <h3 className="font-bold text-lg mb-4" style={{ color: textColor }}>
              Text & Labels
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: textColor }}>
                  Resting State CTA Text
                </label>
                <input
                  type="text"
                  value={localConfig.restingStateCTA}
                  onChange={(e) => handleConfigChange("restingStateCTA", e.target.value)}
                  placeholder="Build Your Dream"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2"
                  style={{
                    focusRingColor: primaryColor,
                  }}
                />
                <p className="text-xs mt-1" style={{ color: profile.brand?.textSecondaryColor || "rgba(0, 0, 0, 0.6)" }}>
                  Text shown in the center button when bar is collapsed
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: textColor }}>
                  Input Placeholder (Optional)
                </label>
                <input
                  type="text"
                  value={localConfig.placeholder}
                  onChange={(e) => handleConfigChange("placeholder", e.target.value)}
                  placeholder="Leave empty for auto-generated placeholder"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2"
                  style={{
                    focusRingColor: primaryColor,
                  }}
                />
                <p className="text-xs mt-1" style={{ color: profile.brand?.textSecondaryColor || "rgba(0, 0, 0, 0.6)" }}>
                  Custom placeholder text for the input field
                </p>
              </div>
            </div>
          </div>

          {/* Position & Size */}
          <div className="p-6 rounded-2xl premium-white-glass backdrop-blur-sm" style={{
            border: `1px solid ${primaryColor}20`,
            boxShadow: `0 4px 16px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.6)`,
          }}>
            <h3 className="font-bold text-lg mb-4" style={{ color: textColor }}>
              Layout
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: textColor }}>
                  Position
                </label>
                <div className="flex gap-2">
                  {(["bottom", "top"] as const).map((pos) => (
                    <button
                      key={pos}
                      onClick={() => handleConfigChange("position", pos)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        localConfig.position === pos
                          ? "text-white"
                          : "text-gray-700"
                      }`}
                      style={
                        localConfig.position === pos
                          ? {
                              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                            }
                          : {
                              background: "rgba(0, 0, 0, 0.05)",
                            }
                      }
                    >
                      {pos.charAt(0).toUpperCase() + pos.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block" style={{ color: textColor }}>
                  Size
                </label>
                <div className="flex gap-2">
                  {(["compact", "medium", "large"] as const).map((size) => (
                    <button
                      key={size}
                      onClick={() => handleConfigChange("size", size)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        localConfig.size === size
                          ? "text-white"
                          : "text-gray-700"
                      }`}
                      style={
                        localConfig.size === size
                          ? {
                              background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                            }
                          : {
                              background: "rgba(0, 0, 0, 0.05)",
                            }
                      }
                    >
                      {size.charAt(0).toUpperCase() + size.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
